import { notification } from 'antd';
import { auth } from 'firebase/firebase';
import moment from 'moment';

import { getRange } from '@src/packages/dashboard/utils';
import { get } from '@src/packages/utils/storage';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { translateString } from '@src/util/intl/translateString';

import { DATE_FROM_DEFAULT } from '../../../constants';
import { buildDeviationsParams, DEVIATIONS_SORT_STORAGE_KEY } from '../transformers/deviationsTaskComplianceTransformer';
import { createBaseActionButtons } from './baseActionButtons';
import { getChartAccess } from './chartAccess';

/**
 * Creates action buttons for deviations from task compliance widgets
 * Includes base buttons with custom export action (CSV by email)
 * The export action triggers backend to generate CSV and send by email
 *
 * @param {Object} params - Parameters for creating action buttons
 * @param {Function} params.handleEdit - Handler for edit action
 * @param {Function} params.handleDelete - Handler for delete action
 * @param {Function} params.setDownloading - Function to set loading state
 * @param {boolean} params.downloading - Whether export is in progress
 * @param {Object} params.widget - Widget object (contains queryParams, title)
 * @param {Object} params.filters - Filter object (contains dateRange, relative, groupBy)
 * @param {Object} params.additionalProps - Additional props (contains orgId, queryDivisionIds, boardSelected)
 * @returns {Array} Array of action button objects
 */
export const getDeviationsActionButtons = ({
  handleEdit,
  handleDelete,
  setDownloading,
  downloading,
  widget,
  filters,
  additionalProps,
}) => {
  const { orgId, queryDivisionIds, boardSelected } = additionalProps;
  const { queryParams = {} } = widget;
  const {
    dateRange,
    relative: { dateType, relativeAmount, isRelative },
    groupBy,
  } = filters;

  // Format date range
  let dateRangeFormated = dateRange || [DATE_FROM_DEFAULT, moment()];
  if (dateRange?.length === 2) {
    dateRangeFormated = [moment(dateRange[0]), moment(dateRange[1])];
  }

  // Build common params (same structure as useGenericTableData)
  const commonParams = {
    uid: auth.currentUser.uid,
    organizationId: orgId,
    range: getRange(dateRangeFormated, dateType, isRelative, relativeAmount),
    interval: groupBy,
    divisionIds: boardSelected?.divId ? [boardSelected.divId] : queryDivisionIds,
  };

  // Get sortFields from storage (same as useDeviationsTableData)
  const sortFields =
    get(DEVIATIONS_SORT_STORAGE_KEY)?.[widget?.id]?.sortFields || [];

  const dashboardParams = buildDeviationsParams(queryParams, commonParams, {
    sortFields,
  });

  const handleExportCsvByEmail = async () => {
    if (setDownloading) {
      setDownloading(true);
    }

    try {
      const exportDeviationsCsv = apiCallable(API_OPERATIONS.EXECUTIONS_DASHBOARD_EXPORT);
      await exportDeviationsCsv({
        ...dashboardParams,
        sendEmail: true,
      });

      notification.success({
        message: translateString('dashboards.deviations.export.success.message'),
      });
    } catch (error) {
      notification.error({
        message: translateString('general.save.error.message'),
      });
    } finally {
      if (setDownloading) {
        setDownloading(false);
      }
    }
  };

  return createBaseActionButtons({
    handleEdit,
    handleDelete,
    handleDownload: handleExportCsvByEmail,
    downloading,
    ...getChartAccess(boardSelected),
    iconSize: 'sm',
  });
};